// src/pages/ia-paca/metiers/ai-product-manager.tsx
//
// Page métier — AI Product Manager en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Target, Users, BarChart3, ShieldCheck, Workflow, Sparkles } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'ai-product-manager',
  metaTitle: 'Recrutement AI Product Manager en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    "Recruter un AI Product Manager en PACA : profils mixtes produit + tech IA, capables de cadrer un POC, prioriser une roadmap IA, parler aux Data Scientists et aux dirigeants. Salaire 62-120k€.",
  metaKeywords:
    'recrutement AI Product Manager, AI PM Marseille, AI Product Manager Sophia, salaire AI PM 2026, recruter Product Manager IA PACA',
  heroLabel: 'AI Product Manager',
  heroSubtitle:
    "Le profil le plus difficile à recruter en IA. Il faut un PM senior qui comprend la tech IA sans être ingénieur, qui sait cadrer un POC, prioriser, embarquer des Data Scientists, parler aux dirigeants — et garder le focus business.",
  heroBadges: ['Roadmap IA', 'Discovery', 'POC sizing', 'KPI IA'],
  jsonLdName: 'AI Product Manager',
  jsonLdDescription:
    "L'AI Product Manager pilote la roadmap des produits et fonctionnalités IA. Pont entre business, Data Scientists, AI Engineers et utilisateurs. Cadre les cas d'usage, priorise les POC, suit la mise en production.",
  jsonLdSkills:
    'Product discovery, KPI design, roadmap IA, ROI estimation, prompt iteration, vector search basics, A/B testing, customer research, design thinking, conduite du changement IA, compréhension MLOps',
  jsonLdMinSalary: 62000,
  jsonLdMedianSalary: 85000,
  jsonLdMaxSalary: 120000,
  jsonLdEducation: 'Background varié : grande école, ingénieur reconverti PM, MBA + tech.',
  jsonLdExperience: '3-10+ ans, junior pur quasi inexistant.',
  intro: {
    title: "Un AI PM, c'est un Product Manager senior + une compréhension fine de l'IA.",
    paragraphs: [
      "Un AI Product Manager n'est ni un Data Scientist qui ferait du produit, ni un PM classique qui aurait suivi une formation IA en 5 jours. C'est un PM senior (idéalement 5+ ans en SaaS B2B ou B2C) qui a passé du temps à comprendre <strong>réellement ce qui change quand on intègre l'IA dans un produit</strong>.",
      "Concrètement : il sait pourquoi un POC IA n'égale pas un produit IA, pourquoi un modèle qui marche à 92% en démo peut échouer en prod, pourquoi un RAG demande une discipline de qualité de données. Il sait cadrer un sizing de POC avec un budget, identifier les KPI métier (pas juste l'accuracy du modèle), et travailler avec les contraintes (latence, coût LLM, hallucinations).",
      "<strong>Le piège du marché :</strong> beaucoup de PM s'autoproclament \"AI PM\" parce qu'ils ont déployé une fonctionnalité ChatGPT dans leur produit. C'est un début, pas une expertise. Notre filtre : nous cherchons des PM qui ont déjà tué un POC IA après mesure (et qui peuvent expliquer pourquoi).",
    ],
  },
  stackTitle: 'Les compétences que votre AI PM doit avoir.',
  stack: [
    {
      Icon: Target,
      title: 'Product foundation',
      tools: ['Discovery', 'Jobs-to-be-Done', 'Story mapping', 'OKR', 'Prioritisation (RICE, MoSCoW)'],
    },
    {
      Icon: BarChart3,
      title: 'Mesure & analytics',
      tools: ['Mixpanel / Amplitude', 'A/B testing (GrowthBook)', 'Eval IA', 'KPI business + KPI modèle', 'CUPED'],
    },
    {
      Icon: Sparkles,
      title: 'Compréhension IA',
      tools: ['LLM APIs (OpenAI, Mistral, Anthropic)', 'RAG', 'Fine-tuning vs prompting', 'Vector search', 'Eval frameworks'],
    },
    {
      Icon: ShieldCheck,
      title: 'Risk & gouvernance',
      tools: ['AI Act (RGPD + classes de risque)', 'Hallucinations', 'Bias', 'PII / sécurité', 'Explicabilité'],
    },
    {
      Icon: Workflow,
      title: 'Workflows & outils',
      tools: ['Linear / Jira', 'Figma', 'Notion', 'Canvas IA (use case canvas)', 'Roadmap MLOps lite'],
    },
    {
      Icon: Users,
      title: 'Soft skills clés',
      tools: ['Storytelling exec', 'Vulgarisation tech', 'Conduite du changement', 'Coordination Data + Eng + UX', 'Frugalité'],
    },
  ],
  salaryByCityTitle: '62-120 k€, packages variables très significatifs (10-30%).',
  salaryByCity: [
    { ville: 'Marseille', junior: '—', confirme: '62 – 85 k€', senior: '85 – 110 k€', expert: '105 – 130 k€' },
    { ville: 'Aix-en-Provence', junior: '—', confirme: '60 – 82 k€', senior: '82 – 108 k€', expert: '100 – 125 k€' },
    { ville: 'Sophia Antipolis', junior: '—', confirme: '68 – 92 k€', senior: '92 – 120 k€', expert: '115 – 140 k€' },
    { ville: 'Nice', junior: '—', confirme: '65 – 88 k€', senior: '88 – 115 k€', expert: '110 – 135 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un AI Product Manager.',
  questionsIntro:
    "Ces questions séparent un AI PM expérimenté d'un PM qui a juste lu 3 articles de Lenny.",
  questions: [
    {
      q: "Décris un projet IA que tu as cadré et lancé. Quels KPI tu suivais, et que s'est-il passé ?",
      good:
        "Cite un cas concret : type de produit, problème métier, solution choisie (et pourquoi), KPI métier + KPI modèle, résultats vs hypothèses. Sait raconter les ajustements en cours de route. Connaît la différence entre métrique de POC et métrique de prod.",
      bad: "Reste théorique ou cite un projet où il/elle n'a fait que de la coordination superficielle. Pas de KPI mesurable, pas d'apprentissage post-launch.",
    },
    {
      q: "Comment tu décides si un cas d'usage mérite un POC IA ou pas ?",
      good:
        "Grille claire : impact métier estimé, données dispo, faisabilité tech (asks aux Data Scientists), risque réglementaire, coût d'opportunité vs roadmap. Sait dire non à un cas séduisant mais peu rentable. Pratique le 'time-boxed POC' (ex. 6 semaines, abandon si pas de signal).",
      bad: "'Si la direction veut tester, on teste.' Pas de cadre, pas de mesure de coût d'opportunité. POC qui dure 6 mois sans décision.",
    },
    {
      q: "Tu travailles avec des Data Scientists et des AI Engineers. Comment tu structures la collab' ?",
      good:
        "Sait expliquer la différence des rôles, leur cycle de travail (POC → industrialisation → MLOps). Travaille en sprint hybride : explorations en parallèle de delivery. Cadre des intermediate goals (data audit, baseline modèle, eval framework). Ne demande pas de l'accuracy 99% le premier mois.",
      bad: "Traite la team Data comme une feature factory. Demande des deadlines sur des explorations. Ne comprend pas les itérations recherche.",
    },
    {
      q: "Tu présentes un projet IA à un comex sceptique. Comment tu structures ton pitch ?",
      good:
        "Démarre par le problème business + les chiffres d'impact. Présente la solution IA comme un moyen (pas la fin). Quantifie les risques (faux positifs, hallucinations, coûts récurrents). Propose un plan de déploiement progressif et un kill switch. Anticipe les questions sur RGPD / AI Act / sécurité.",
      bad: "Démarre par 'on va utiliser un LLM'. Met l'accent sur la techno, pas sur la valeur. Ignore les risques, promet trop.",
    },
    {
      q: "Comment tu prends en compte l'AI Act dans ta roadmap ?",
      good:
        "Connaît les grandes classes de risque (interdit / haut risque / limité / minimal). Sait identifier si son produit tombe dans une catégorie sensible (RH, scoring, santé, biométrie). Travaille avec le juridique pour cadrer documentation, transparence, contrôle humain.",
      bad: "'L'AI Act c'est pour les grosses boîtes.' Ignorance du sujet, risque pour l'entreprise.",
    },
  ],
  ctaTitle: 'Vous cherchez un AI Product Manager en PACA ?',
  ctaSubtitle:
    "Profil très rare en PACA. Notre court-list mixe profils locaux et profils en relocalisation depuis Paris. 4-6 candidats qualifiés en 20-30 jours.",
};

export default function AiProductManagerPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
